import { useQueryClient } from "@tanstack/solid-query";
import { For, Show, createMemo, createSignal } from "solid-js";

import { type CsvEntityPlural, type CsvImportResponse, downloadCsv, uploadCsv } from "~/lib/csv_io";

type Props = {
  wsKey: () => string;
  plural: CsvEntityPlural;
};

// Choices the UI surfaces for the upsert key. Must be a subset of the
// backend's adapter.upsert_keys whitelist (ops/import_export.py). We only
// surface the human-friendly natural-key option per entity; the
// {scaikey_partner_id, scaicontrol_customer_id, …} variants exist on the
// backend for programmatic syncs but aren't useful in this UI.
type UpsertChoice = { value: string; label: string };
const UPSERT_CHOICES: Record<CsvEntityPlural, UpsertChoice[]> = {
  accounts: [{ value: "primary_domain", label: "Primary domain" }],
  contacts: [{ value: "primary_email", label: "Primary email" }],
  deals: [],
  products: [{ value: "sku", label: "SKU" }],
  // Composite key (source_system + source_ref) backed by
  // `uq_activity_workspace_source` — both columns must be present in
  // every row when this key is picked. Rows lacking source_ref stay
  // create-only.
  activities: [
    {
      value: "source_system+source_ref",
      label: "External source ref (source_system + source_ref)",
    },
  ],
};

export function CsvIoButtons(props: Props) {
  const qc = useQueryClient();
  const [exporting, setExporting] = createSignal(false);
  const [exportError, setExportError] = createSignal<string | null>(null);

  const [showImport, setShowImport] = createSignal(false);
  const [file, setFile] = createSignal<File | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [importError, setImportError] = createSignal<string | null>(null);
  const [result, setResult] = createSignal<CsvImportResponse | null>(null);
  const [upsertKey, setUpsertKey] = createSignal<string>("");

  const choices = createMemo(() => UPSERT_CHOICES[props.plural]);

  const reset = () => {
    setFile(null);
    setResult(null);
    setImportError(null);
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadCsv(props.wsKey(), props.plural);
    } catch (err) {
      setExportError((err as Error).message);
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (dryRun: boolean) => {
    const f = file();
    if (!f) return;
    setBusy(true);
    setImportError(null);
    try {
      const r = await uploadCsv(props.wsKey(), props.plural, f, {
        dryRun,
        upsertKey: upsertKey() || null,
      });
      setResult(r);
      if (!dryRun && r.created + r.updated > 0) {
        qc.invalidateQueries({ queryKey: [props.plural, props.wsKey()] });
      }
    } catch (err) {
      setImportError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setShowImport(false);
    reset();
  };

  const lastResultWasDryRun = () => result()?.dry_run === true;
  const canCommit = () => {
    const r = result();
    if (!r) return false;
    return r.dry_run && r.created + r.updated > 0 && !busy();
  };

  return (
    <>
      <button
        type="button"
        class="btn btn-sm btn-ghost"
        onClick={handleExport}
        disabled={exporting()}
        aria-label={`Export ${props.plural} as CSV`}
      >
        {exporting() ? "Exporting…" : "Export CSV"}
      </button>
      <button
        type="button"
        class="btn btn-sm btn-ghost"
        onClick={() => setShowImport(true)}
        aria-label={`Import ${props.plural} from CSV`}
      >
        Import CSV
      </button>

      <Show when={exportError()}>
        <div role="alert" class="alert alert-error text-sm absolute top-16 right-4 max-w-xl">
          {exportError()}
        </div>
      </Show>

      <Show when={showImport()}>
        <dialog open class="modal modal-open" aria-modal="true" aria-labelledby="csv-import-title">
          <div class="modal-box max-w-2xl">
            <h3 id="csv-import-title" class="font-bold text-lg mb-3">
              Import {props.plural} from CSV
            </h3>
            <p class="text-sm opacity-70 mb-4">
              Column headers are tolerant of case, spaces, and dashes — <code>Primary Domain</code>,{" "}
              <code>primary-domain</code> and <code>primary_domain</code> all match. Empty cells use
              schema defaults; <code>cf:&lt;key&gt;</code> columns set custom fields.{" "}
              <strong>Always preview with a dry-run first.</strong>
            </p>

            <div class="grid gap-3 sm:grid-cols-2 mb-4">
              <div class="form-control">
                <label class="label" for="csv-import-file">
                  <span class="label-text">CSV file</span>
                </label>
                <input
                  id="csv-import-file"
                  type="file"
                  accept=".csv,text/csv"
                  class="file-input file-input-bordered file-input-sm w-full"
                  onChange={(e) => {
                    setFile(e.currentTarget.files?.[0] ?? null);
                    setResult(null);
                  }}
                />
              </div>
              <Show when={choices().length > 0}>
                <div class="form-control">
                  <label class="label" for="csv-import-upsert-key">
                    <span class="label-text">Match existing rows by</span>
                  </label>
                  <select
                    id="csv-import-upsert-key"
                    class="select select-bordered select-sm"
                    value={upsertKey()}
                    onChange={(e) => {
                      setUpsertKey(e.currentTarget.value);
                      setResult(null);
                    }}
                  >
                    <option value="">— none (create only) —</option>
                    <For each={choices()}>{(c) => <option value={c.value}>{c.label}</option>}</For>
                  </select>
                </div>
              </Show>
            </div>

            <Show when={importError()}>
              <div role="alert" class="alert alert-error text-sm mb-3">
                {importError()}
              </div>
            </Show>

            <Show when={result()}>
              {(r) => (
                <div class="mb-4">
                  <div class="stats stats-horizontal w-full text-sm shadow-sm">
                    <div class="stat py-2">
                      <div class="stat-title">Total</div>
                      <div class="stat-value text-lg">{r().total}</div>
                    </div>
                    <div class="stat py-2">
                      <div class="stat-title">Created</div>
                      <div class="stat-value text-lg text-success">{r().created}</div>
                    </div>
                    <div class="stat py-2">
                      <div class="stat-title">Updated</div>
                      <div class="stat-value text-lg text-info">{r().updated}</div>
                    </div>
                    <div class="stat py-2">
                      <div class="stat-title">Rejected</div>
                      <div class="stat-value text-lg text-error">{r().rejected}</div>
                    </div>
                    <div class="stat py-2">
                      <div class="stat-title">Mode</div>
                      <div class="stat-value text-lg">{r().dry_run ? "Preview" : "Committed"}</div>
                    </div>
                  </div>
                  <Show when={r().upsert_key}>
                    <p class="text-xs opacity-60 mt-2">
                      Upsert key: <code>{r().upsert_key}</code>
                    </p>
                  </Show>
                  <Show when={r().rejected > 0}>
                    <div
                      class="mt-3 max-h-64 overflow-y-auto bg-base-200 rounded-lg p-2"
                      aria-label="Rejected rows"
                    >
                      <table class="table table-xs">
                        <thead>
                          <tr>
                            <th>Line</th>
                            <th>Result</th>
                            <th>Detail</th>
                          </tr>
                        </thead>
                        <tbody>
                          <For each={r().rows.filter((row) => !row.ok)}>
                            {(row) => (
                              <tr>
                                <td>{row.line_no}</td>
                                <td>
                                  <span class="badge badge-error badge-xs">error</span>
                                </td>
                                <td class="text-xs">{row.error}</td>
                              </tr>
                            )}
                          </For>
                        </tbody>
                      </table>
                    </div>
                  </Show>
                </div>
              )}
            </Show>

            <div class="modal-action">
              <button type="button" class="btn btn-ghost" onClick={close} disabled={busy()}>
                Close
              </button>
              <button
                type="button"
                class="btn"
                onClick={() => handleImport(true)}
                disabled={!file() || busy()}
              >
                {busy() && lastResultWasDryRun() ? "Validating…" : "Preview (dry-run)"}
              </button>
              <button
                type="button"
                class="btn btn-primary"
                onClick={() => handleImport(false)}
                disabled={!canCommit()}
              >
                {busy() && !lastResultWasDryRun() ? "Committing…" : "Commit"}
              </button>
            </div>
          </div>
        </dialog>
      </Show>
    </>
  );
}
